/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Modal,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Insights } from '@mui/icons-material';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { getFormatedTimeByTimeZone } from '../../../utils/general';
import { TimeFormatEnum } from '../../../api-client/generated';
import useAuthStore from '../../../store/zustand/auth';
import ImageCarousel from './components/ImageCarousel';
import ImageViewer from '../../../components/ImageViewer';
import useNavigationStore from '../../../store/zustand/navigation';
import api from '../../../api';
import useNotificationsStore from '../../../store/zustand/notifications';

function SectionLabel({ label }: { label: string }) {
  return (
    <Typography
      sx={{
        color: '#7D8090',
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.08em',
        mb: '6px',
        textTransform: 'uppercase'
      }}
    >
      {label}
    </Typography>
  );
}

function AuditRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        '&:last-child': { borderBottom: 'none' },
        borderBottom: '1px solid #F5F6F8',
        display: 'flex',
        justifyContent: 'space-between',
        pb: '5px',
        pt: '5px'
      }}
    >
      <Typography sx={{ color: '#7D8090', fontSize: '12px' }}>
        {label}
      </Typography>
      <Typography sx={{ color: '#0f1c2e', fontSize: '12px', fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
}

interface Props {
  callBack: () => void;
  iteration: any;
  technician: any;
}

const ProjectTaskDetails: React.FC<Props> = function ProjectTaskDetails({
  iteration,
  technician,
  callBack
}) {
  const [checklistModalOpen, setChecklistModalOpen] = useState(false);
  const [completeApi, setCompleteApi] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [viewerImageUrl, setViewerImageUrl] = useState('');
  const [viewerImageIndex, setViewerImageIndex] = useState(0);

  const { t } = useTranslation('translation', {
    keyPrefix: 'PROJECT.TASKDETAILSMODAL'
  });
  const { t: translation } = useTranslation();
  const { setConfirmModal } = useNavigationStore((state) => state);
  const userState = useAuthStore((state) => state);
  const navigate = useNavigate();
  const { addNotification } = useNotificationsStore((state) => state);

  const { data: completeData, error: completeError } = useSWR(
    completeApi
      ? { taskIterationId: iteration?.id, technicianId: technician?.id }
      : null,
    api.CalendarApi.patchTechnicianTaskManagenmentCloseTask
  );

  const fmt = (value: any) => {
    if (typeof value === 'boolean')
      return value ? t('confirmed') : t('noConfirmed');
    return value || '—';
  };

  useEffect(() => {
    if (completeData) {
      setCompleteApi(false);
      addNotification(translation('PROJECT.TOAST.SUCCESS'), 'success');
      callBack();
    }
  }, [completeData]);

  useEffect(() => {
    if (completeError) {
      setCompleteApi(false);
      addNotification(translation('PROJECT.TOAST.ERROR'), 'error');
    }
  }, [completeError]);

  const geoApproved = iteration?.geofencingApproved;
  const geoLat = iteration?.geofencingLat ?? iteration?.latitude;
  const geoLng = iteration?.geofencingLong ?? iteration?.longitude;
  const checkedCount =
    iteration?.checkList?.filter((i: any) => i.checked).length ?? 0;
  const totalCount = iteration?.checkList?.length ?? 0;
  const checklistProgress =
    totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;
  const startTime = getFormatedTimeByTimeZone(
    iteration?.startTime,
    0,
    userState.timeFormat === TimeFormatEnum._24h
  );
  const endTime = getFormatedTimeByTimeZone(
    iteration?.endTime,
    0,
    userState.timeFormat === TimeFormatEnum._24h
  );

  return (
    <Box
      sx={{
        backgroundColor: '#FAFBFC',
        borderTop: '1px solid #E2E4EA',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: '16px 20px',
        width: '100%'
      }}
    >
      {/* 3-column layout */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* ACTIONS */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 148 }}>
          <SectionLabel label="Actions" />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Button
              onClick={() => {
                window.location.href = `tel:${technician?.phoneNumber}`;
              }}
              size="small"
              sx={{
                fontSize: '12px',
                gap: '6px',
                height: 32,
                justifyContent: 'flex-start',
                textTransform: 'none',
                width: '100%'
              }}
              variant="secondary-outlined"
            >
              <i
                className="custom-icon icon-classic-phone"
                style={{ fontSize: 13 }}
              />
              {t('callTechnician')}
            </Button>
            <Button
              onClick={() => {
                navigate(`/app/resources/insights/${technician?.id}`, {
                  state: { user: { ...technician } }
                });
              }}
              size="small"
              sx={{
                fontSize: '12px',
                gap: '6px',
                height: 32,
                justifyContent: 'flex-start',
                textTransform: 'none',
                width: '100%'
              }}
              variant="secondary-outlined"
            >
              <Insights sx={{ fontSize: 14 }} />
              {t('technicianInsights')}
            </Button>
          </Box>
        </Box>

        {/* GEOFENCING + CHECKLIST */}
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 200px',
            flexDirection: 'column',
            maxWidth: 300
          }}
        >
          <SectionLabel label="Geofencing" />
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '6px',
              mb: '4px'
            }}
          >
            {geoApproved === true && (
              <>
                <i
                  className="custom-icon icon-check-circle"
                  style={{ color: '#189A90', fontSize: 14 }}
                />
                <Typography
                  sx={{ color: '#189A90', fontSize: '13px', fontWeight: 600 }}
                >
                  Approved
                </Typography>
              </>
            )}
            {geoApproved === false && (
              <>
                <i
                  className="custom-icon icon-close-circle"
                  style={{ color: '#E79137', fontSize: 14 }}
                />
                <Typography
                  sx={{ color: '#E79137', fontSize: '13px', fontWeight: 600 }}
                >
                  Unconfirmed
                </Typography>
              </>
            )}
            {geoApproved == null && (
              <Typography sx={{ color: '#7D8090', fontSize: '12px' }}>
                —
              </Typography>
            )}
          </Box>
          {(geoLat || geoLng) && (
            <Typography sx={{ color: '#7D8090', fontSize: '11px', mb: '2px' }}>
              Lat: {geoLat} · Long: {geoLng}
            </Typography>
          )}
          <Typography sx={{ color: '#7D8090', fontSize: '10px', mb: '12px' }}>
            Single geofencingApproved boolean — not split check-in/out
          </Typography>
          <SectionLabel label="Checklist" />
          <Box
            sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: '6px' }}
          >
            <Typography
              sx={{
                color: '#0f1c2e',
                fontSize: '22px',
                fontWeight: 700,
                lineHeight: 1
              }}
            >
              {checkedCount} / {totalCount}
            </Typography>
            <Button
              onClick={() => setChecklistModalOpen(true)}
              size="small"
              sx={{
                fontSize: '11px',
                gap: '4px',
                height: 24,
                minWidth: 0,
                px: '8px',
                textTransform: 'none'
              }}
              variant="secondary-outlined"
            >
              <i className="custom-icon icon-eye" style={{ fontSize: 12 }} />
              View items
            </Button>
          </Box>
          <LinearProgress
            sx={{
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#189A90',
                borderRadius: '99px'
              },
              backgroundColor: '#E2E4EA',
              borderRadius: '99px',
              height: 5
            }}
            value={checklistProgress}
            variant="determinate"
          />
        </Box>

        {/* AUDIT TRAIL */}
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 200px',
            flexDirection: 'column',
            maxWidth: 300
          }}
        >
          <SectionLabel label="Audit Trail" />
          <Box
            sx={{
              backgroundColor: '#fff',
              border: '1px solid #E2E4EA',
              borderRadius: '8px',
              overflow: 'hidden',
              px: '12px',
              py: '4px'
            }}
          >
            <AuditRow label="Start time" value={startTime || '—'} />
            <AuditRow label="End time" value={endTime || '—'} />
            <AuditRow label="Started by" value={fmt(iteration?.startedBy)} />
            <AuditRow
              label="Starter"
              value={fmt(
                iteration?.starter
                  ? `${iteration.starter.firstName} ${iteration.starter.lastName}`
                  : undefined
              )}
            />
            <AuditRow label="Closed by" value={fmt(iteration?.closedBy)} />
            <AuditRow
              label="Closer"
              value={fmt(
                iteration?.closer
                  ? `${iteration.closer.firstName} ${iteration.closer.lastName}`
                  : undefined
              )}
            />
          </Box>
        </Box>
      </Box>

      {/* Images */}
      {iteration?.fileBank?.length > 0 && (
        <Box sx={{ borderTop: '1px solid #E2E4EA', pt: 1.5 }}>
          <Typography
            sx={{
              color: '#005DA2',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              mb: 1,
              textDecoration: 'underline'
            }}
          >
            Images ({iteration.fileBank.length})
          </Typography>
          <ImageCarousel
            images={iteration.fileBank}
            openViewer={(val: boolean) => setIsImageViewerOpen(val)}
            setImageUrlViewer={(url: string) => {
              setViewerImageUrl(url);
              const idx = iteration.fileBank.findIndex(
                (img: any) => (img.large || img.fileUrl) === url
              );
              setViewerImageIndex(idx >= 0 ? idx : 0);
            }}
          />
          {isImageViewerOpen && viewerImageUrl && (
            <ImageViewer
              isOpen={isImageViewerOpen}
              onNext={
                viewerImageIndex < iteration.fileBank.length - 1
                  ? () => {
                      const next = viewerImageIndex + 1;
                      setViewerImageIndex(next);
                      setViewerImageUrl(
                        iteration.fileBank[next]?.large ||
                          iteration.fileBank[next]?.fileUrl
                      );
                    }
                  : undefined
              }
              onPrev={
                viewerImageIndex > 0
                  ? () => {
                      const prev = viewerImageIndex - 1;
                      setViewerImageIndex(prev);
                      setViewerImageUrl(
                        iteration.fileBank[prev]?.large ||
                          iteration.fileBank[prev]?.fileUrl
                      );
                    }
                  : undefined
              }
              setIsOpen={setIsImageViewerOpen}
              url={viewerImageUrl}
            />
          )}
        </Box>
      )}

      {/* Complete Task */}
      {iteration?.status !== 'completed' && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
          <Button
            disabled={completeApi}
            onClick={() => {
              setConfirmModal(
                async () => {
                  setCompleteApi(true);
                },
                `${translation('GENERAL.confirmButton')}`,
                () => {},
                `${translation('GENERAL.cancelButton')}`,
                <Box
                  alignItems="center"
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  justifyContent="center"
                  pt={2}
                  width={350}
                >
                  <Typography variant="heading_500">
                    {t('completeTask')}
                  </Typography>
                  <Typography
                    sx={{ whiteSpace: 'pre-line' }}
                    variant="body_300"
                  >
                    {t('completeTaskDescription')}
                  </Typography>
                </Box>
              );
            }}
            sx={{ height: 36, minWidth: 160 }}
            variant="primary-outlined"
          >
            <Box alignItems="center" display="flex" gap={1}>
              {completeApi ? (
                <CircularProgress size={13} />
              ) : (
                <Typography fontSize="13px" variant="heading_328">
                  {t('completeTaskButton')}
                </Typography>
              )}
            </Box>
          </Button>
        </Box>
      )}

      {/* Checklist Modal */}
      <Modal
        onClose={() => setChecklistModalOpen(false)}
        open={checklistModalOpen}
        sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}
      >
        <Box
          bgcolor="#fff"
          borderRadius={2}
          boxShadow={4}
          maxWidth={400}
          minWidth={320}
          p={3}
        >
          <Typography fontWeight={700} mb={2} sx={{ fontSize: '14px' }}>
            {t('checklistDetails')}
          </Typography>
          <Box
            maxHeight={300}
            overflow="auto"
            sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            {totalCount > 0 ? (
              iteration.checkList.map((item: any) => (
                <Box alignItems="center" display="flex" gap={1} key={item.id}>
                  <i
                    className={`custom-icon icon-${
                      item?.checked ? 'active' : 'inactive'
                    }`}
                    style={{
                      color: item.checked ? '#189A90' : '#CBCCD3',
                      fontSize: 14
                    }}
                  />
                  <Typography sx={{ color: '#4B4D56', fontSize: '13px' }}>
                    {item.name}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
                {t('checklistModalNoItems')}
              </Typography>
            )}
          </Box>
          <Box display="flex" mt={2}>
            <Button
              fullWidth
              onClick={() => setChecklistModalOpen(false)}
              size="small"
              variant="outlined"
            >
              {t('checklistModalClose')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ProjectTaskDetails;
